/**
 * utils/cars.js
 * Pure functions for managing the canonical cars list and keeping item fitments in sync.
 * All name comparisons are case-insensitive; canonical stored names are preserved.
 * Only data.cars and data.items[].fitment are touched — history/transformations/recipes
 * are always passed through unchanged.
 */

function dedupeNames(arr) {
  const seen = new Set();
  return arr.filter((n) => {
    const key = n.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Merge fromName into intoName: replace every fitment occurrence of fromName with intoName
 * (case-insensitive), dedupe per item, then remove fromName from cars.
 * No-op when fromName and intoName are equal (case-insensitive).
 */
export function mergeCar(data, fromName, intoName) {
  const fromLower = (fromName || "").trim().toLowerCase();
  const intoLower = (intoName || "").trim().toLowerCase();
  if (!fromLower || fromLower === intoLower) return data;

  const intoCar = (data.cars || []).find((c) => c.name.toLowerCase() === intoLower);
  const canonicalInto = intoCar ? intoCar.name : (intoName || "").trim();

  const items = (data.items || []).map((it) => {
    const next = (it.fitment || []).map((f) =>
      f.toLowerCase() === fromLower ? canonicalInto : f
    );
    return { ...it, fitment: dedupeNames(next) };
  });

  const cars = (data.cars || []).filter((c) => c.name.toLowerCase() !== fromLower);

  return { ...data, cars, items };
}

/**
 * Rename oldName to newName in cars and all item fitments.
 * - blank newName or same name (case-insensitive) → no-op.
 * - newName already exists as a different car → delegate to mergeCar (rename-into-existing).
 * - otherwise: update the cars entry and rewrite item fitments, deduping per item.
 */
export function renameCar(data, oldName, newName) {
  const nm = (newName || "").trim();
  const oldLower = (oldName || "").trim().toLowerCase();
  if (!nm || nm.toLowerCase() === oldLower) return data;

  const existingNew = (data.cars || []).find((c) => c.name.toLowerCase() === nm.toLowerCase());
  if (existingNew) {
    return mergeCar(data, oldName, existingNew.name);
  }

  const cars = (data.cars || []).map((c) =>
    c.name.toLowerCase() === oldLower ? { name: nm } : c
  );
  const items = (data.items || []).map((it) => {
    const next = (it.fitment || []).map((f) =>
      f.toLowerCase() === oldLower ? nm : f
    );
    return { ...it, fitment: dedupeNames(next) };
  });

  return { ...data, cars, items };
}

/**
 * Remove name from cars and strip it (case-insensitive) from every item's fitment.
 * Items may end with an empty fitment array.
 */
export function deleteCar(data, name) {
  const nameLower = (name || "").trim().toLowerCase();

  const cars = (data.cars || []).filter((c) => c.name.toLowerCase() !== nameLower);
  const items = (data.items || []).map((it) => ({
    ...it,
    fitment: (it.fitment || []).filter((f) => f.toLowerCase() !== nameLower),
  }));

  return { ...data, cars, items };
}

/** Number of items whose fitment contains name (case-insensitive). */
export function countItemsUsingCar(data, name) {
  const nameLower = (name || "").trim().toLowerCase();
  return (data.items || []).filter((it) =>
    (it.fitment || []).some((f) => f.toLowerCase() === nameLower)
  ).length;
}
